import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory} from 'react-router-dom';

import {exportReturnAuditLog, fetchReturnAuditLog} from 'api/returnAudit';
import {auditColumns} from 'components/common/AuditLog/columns';
import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {PageContent, SearchFields, Toolbar} from 'components/common/AuditLog/styled';
import {useAuditLog} from 'components/common/AuditLog/useAuditLog';
import {Page} from 'components/common/Page';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {RETURN_AUDIT_EVENTS} from 'types/return-audit';

import {mapReturnAuditFilters} from './filters';

export {ReturnAuditDetailPage} from './Detail';

export const ReturnAuditLogPage = () => {
    const {t} = useTranslation();
    const history = useHistory();
    const permissions = useStore(permissions$);
    const {
        filters,
        page,
        size,
        data,
        pending,
        setPage,
        setSize,
        changeFilters,
        resetFilters,
    } = useAuditLog(fetchReturnAuditLog, mapReturnAuditFilters);
    const fields = auditColumns(
        t,
        RETURN_AUDIT_EVENTS.map((value) => ({
            value,
            label: t(`pages.returnAudit.events.${value}`),
        })),
        true,
    );
    const reasons = [
        'RETURN_FROM_CONSUMER',
        'EXPIRATION',
        'SPOILAGE',
        'PACKAGING_DAMAGE',
        'DESTRUCTION',
    ].map((value) => ({value, label: t(`dictionaries.documents.returnGoods.reasons.${value}`)}));
    const rows = (data?.data || []).map((entry) => ({
        ...entry,
        entry,
        eventId: (
            <Link to={generatePath(ROUTE.RETURN_AUDIT_DETAIL, {id: entry.eventId})}>
                {entry.eventId}
            </Link>
        ),
        eventType: t(`pages.returnAudit.events.${entry.eventType}`, {
            defaultValue: entry.eventType,
        }),
        eventTimestamp: formatLocalDateTime(entry.eventTimestamp),
        userRole: translateUserRole(t, entry.userRole),
        userFullName: entry.userFullName || entry.userId || '—',
        organizationName: entry.organizationName ?? entry.organizationId ?? '—',
        organizationTin: entry.organizationTin || '—',
    }));
    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation />
                <Table
                    headerControls={
                        <Toolbar>
                            <SearchFields>
                                {['markingCode', 'documentId'].map((key) => (
                                    <Input
                                        key={key}
                                        placeholder={t(`pages.returnAudit.fields.${key}`)}
                                        aria-label={t(`pages.returnAudit.fields.${key}`)}
                                        value={String(filters[key] || '')}
                                        onChange={(event) =>
                                            changeFilters([{id: key, value: event.target.value}])
                                        }
                                    />
                                ))}
                                <Select
                                    isClearable
                                    options={reasons}
                                    placeholder={t('pages.returnAudit.fields.disposalReason')}
                                    value={
                                        reasons.find(
                                            ({value}) => value === filters.disposalReason,
                                        ) || null
                                    }
                                    onChange={(option: SelectOption<string> | null) =>
                                        changeFilters([
                                            {id: 'disposalReason', value: option?.value || ''},
                                        ])
                                    }
                                />
                            </SearchFields>
                            {permissions.includes(PERMISSION_ALIASES.returnAuditLogExport) && (
                                <AuditExportControl
                                    fileName="return-audit"
                                    initialPeriod={
                                        filters.eventTimestamp as [Date | null, Date | null]
                                    }
                                    onExport={(format, period) => {
                                        const params = mapReturnAuditFilters({
                                            ...filters,
                                            eventTimestamp: period,
                                        });
                                        return exportReturnAuditLog({
                                            ...params,
                                            from: params.from!,
                                            to: params.to!,
                                            page,
                                            size,
                                            format,
                                        });
                                    }}
                                />
                            )}
                        </Toolbar>
                    }
                    columns={mapTableColumns(
                        fields,
                        filters,
                        changeFilters,
                        resetFilters,
                        {
                            eventTimestamp:
                                filters.sortDesc === undefined
                                    ? undefined
                                    : filters.sortDesc === 'true',
                        },
                        (values) =>
                            changeFilters(
                                values.map(({value}) => ({
                                    id: 'sortDesc',
                                    value: value === undefined ? undefined : String(value),
                                })),
                            ),
                    )}
                    data={rows}
                    pageIndex={page}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => {
                        setSize(value);
                        setPage(0);
                    }}
                    isLoading={pending}
                    onRowClick={(row) =>
                        history.push(
                            generatePath(ROUTE.RETURN_AUDIT_DETAIL, {
                                id: row.original.entry.eventId,
                            }),
                        )
                    }
                />
            </PageContent>
        </Page>
    );
};
